import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useFetchVendors } from '../../../hooks/toolbox-hooks';
import GtmContentCrud, { GtmContentPermissions } from './GtmContentCrud';
import { GtmOptionSources } from './GtmContentFields';
import { GTM_PAGE_CONTENT_KEYS, gtmContentSpec } from './gtmContentSpecs';

/**
 * Everything the architecture page renders as copy, one collapsed panel per table.
 *
 * The rows come from the go-to-market record the editor already loaded, so opening this costs
 * nothing; only the vendor list for the competitor and comparison selects is fetched.
 */

const GtmPageContentSection: React.FC<
  GtmContentPermissions & {
    gtmId: number | null;
    /** The detailed `db/go-to-market/<id>` payload. */
    gtm: any;
    loading?: boolean;
  }
> = ({ gtmId, gtm, loading = false, readOnly, canCreate, canUpdate, canDelete }) => {
  const { data: vendors } = useFetchVendors();

  const optionSources: GtmOptionSources = useMemo(() => {
    const comparisonVendors = [...(gtm?.comparison_vendor || [])].sort(
      (a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0) || (a?.id ?? 0) - (b?.id ?? 0)
    );

    return {
      vendors: (vendors || [])
        .map((vendor: any) => ({ value: vendor.id, label: vendor.name }))
        .sort((a: any, b: any) => String(a.label).localeCompare(String(b.label))),
      // The comparison table's columns, so a cell can only point at a column that exists.
      comparisonVendors: comparisonVendors.map((row: any) => ({
        value: row.id,
        label: row.vendor_name || row.vendor?.name || `#${row.id}`,
      })),
    };
  }, [vendors, gtm?.comparison_vendor]);

  const specs = GTM_PAGE_CONTENT_KEYS.map(gtmContentSpec);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Architecture Page Content
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        The copy the public architecture page renders for this motion. Products, components and use
        cases stay in the panels above.
      </Typography>

      {gtmId === null ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Save the go-to-market first, then its page content can be added here.
        </Alert>
      ) : (
        <Box mt={2}>
          {specs.map((spec) => {
            const rows = gtm?.[spec.rowsProperty] || [];

            return (
              <Accordion key={spec.key} disableGutters TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">{spec.title}</Typography>
                    <Chip
                      size="small"
                      label={rows.length}
                      color={rows.length ? 'primary' : 'default'}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <GtmContentCrud
                    spec={spec}
                    parentId={gtmId}
                    rows={rows}
                    optionSources={optionSources}
                    description={spec.description}
                    hideTitle
                    loading={loading}
                    readOnly={readOnly}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default GtmPageContentSection;
